import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  ElementRef,
  viewChild,
} from '@angular/core';
import { Modal } from 'flowbite';
import moment from 'moment';
import { IClient, IHolidayCalendar } from 'src/app/models';
import { UserRole } from 'src/app/models/enums';
import { HolidayCalendarsService, UsersService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';

interface CalendarCell {
  date: string;
  day: number;
  selected: boolean;
  isToday: boolean;
}

@Component({
  selector: 'app-modal-holiday-calendar',
  templateUrl: './modal-holiday-calendar.component.html',
  styleUrl: './modal-holiday-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalHolidayCalendarComponent {
  @Input() id: string;
  @Input() modal: Modal;
  @Input() clientId: number | undefined;
  @Input() userRole: UserRole;
  @Output() action = new EventEmitter<any>();
  @Output() deleteHolidayCalendarEvent = new EventEmitter<IHolidayCalendar>();

  readonly modalContainer = viewChild.required<ElementRef<HTMLElement>>('modalContainer');

  public images = images;
  public UserRole = UserRole;

  public holidayCalendars: IHolidayCalendar[] = [];
  public searchValue = '';
  public page = 0;
  public size = 20;
  public hasMore = true;
  public loading = false;

  public isCreating = false;
  public isEditing = false;
  public confirmingDelete = false;

  public editingCalendar: IHolidayCalendar = this.getEmptyCalendar();
  public editingHolidays: Set<string> = new Set();

  public currentMonth = moment().startOf('month');
  public weekDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  public clients: IClient[] = [];
  private clientsLoaded = false;

  constructor(
    public literalService: LiteralService,
    private holidayCalendarsService: HolidayCalendarsService,
    private usersService: UsersService,
  ) {}

  private async ensureClientsLoaded(): Promise<void> {
    if (this.clientsLoaded) return;
    try {
      const res = await this.usersService.getClients();
      this.clients = Array.isArray(res) ? res : [];
    } catch (error) {
      console.error('Error loading clients:', error);
      this.clients = [];
    } finally {
      this.clientsLoaded = true;
    }
  }

  get showClientSelector(): boolean {
    return this.clients.length > 1 || this.userRole === UserRole.SUPERADMIN;
  }

  get clientOptions(): any[] {
    const opts = this.clients.map((c) => ({
      id: c.id,
      value: c.id,
      label: c.name,
    }));
    if (this.userRole === UserRole.SUPERADMIN) {
      return [
        {
          id: null,
          value: null,
          label: this.literalService.get(
            'setup.holidayCalendars.noClient',
            true,
          ),
        },
        ...opts,
      ];
    }
    return opts;
  }

  onClientChange(event: any) {
    const value = event?.value ?? event?.options?.[0]?.value ?? null;
    this.editingCalendar.clientId = value;
  }

  async load(reset = true): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    try {
      if (reset) {
        this.page = 0;
        this.holidayCalendars = [];
        this.hasMore = true;
      }
      const results = await this.holidayCalendarsService.getHolidayCalendars(
        this.searchValue,
        this.page,
        this.size,
      );
      const list = Array.isArray(results) ? results : [];
      this.holidayCalendars = reset
        ? list
        : [...this.holidayCalendars, ...list];
      this.hasMore = list.length === this.size;
    } catch (error) {
      console.error('Error loading holiday calendars:', error);
    } finally {
      this.loading = false;
    }
  }

  async onSearchChange(event: any) {
    this.searchValue = event.target?.value ?? '';
    await this.load(true);
  }

  async onScrollEnd() {
    if (!this.hasMore || this.loading) return;
    this.page += 1;
    await this.load(false);
  }

  getEmptyCalendar(): IHolidayCalendar {
    let initialClientId: number | null | undefined = this.clientId;
    if (this.clients?.length === 1) initialClientId = this.clients[0].id;
    return {
      id: undefined,
      name: '',
      clientId: initialClientId,
      editable: true,
      holidays: [],
    };
  }

  async startCreating() {
    await this.ensureClientsLoaded();
    this.isCreating = true;
    this.isEditing = false;
    this.editingCalendar = this.getEmptyCalendar();
    this.editingHolidays = new Set();
    this.currentMonth = moment().startOf('month');
  }

  async startEditing(calendar: IHolidayCalendar) {
    if (!calendar.id) return;
    await this.ensureClientsLoaded();
    this.isCreating = false;
    this.isEditing = true;
    try {
      const full = await this.holidayCalendarsService.getHolidayCalendar(
        calendar.id,
      );
      this.editingCalendar = { ...full };
      this.editingHolidays = new Set(full.holidays ?? []);
    } catch {
      this.editingCalendar = { ...calendar, holidays: [] };
      this.editingHolidays = new Set();
    }
    this.currentMonth = moment().startOf('month');
  }

  cancelEdit() {
    this.isCreating = false;
    this.isEditing = false;
    this.confirmingDelete = false;
    this.editingCalendar = this.getEmptyCalendar();
    this.editingHolidays = new Set();
  }

  resetState(): void {
    this.isCreating = false;
    this.isEditing = false;
    this.confirmingDelete = false;
    this.editingCalendar = this.getEmptyCalendar();
    this.editingHolidays = new Set();
    this.searchValue = '';
    this.currentMonth = moment().startOf('month');
    this.holidayCalendars = [];
    this.page = 0;
    this.hasMore = true;
  }

  closeModal() {
    this.confirmingDelete = false;
    this.modal.hide();
  }

  updateName(event: any) {
    this.editingCalendar.name = event.target?.value ?? '';
  }

  prevMonth() {
    this.currentMonth = this.currentMonth.clone().subtract(1, 'month');
  }

  nextMonth() {
    this.currentMonth = this.currentMonth.clone().add(1, 'month');
  }

  get monthLabel(): string {
    return this.currentMonth.format('MMMM YYYY');
  }

  get calendarCells(): (CalendarCell | null)[] {
    const cells: (CalendarCell | null)[] = [];
    const todayStr = moment().format('YYYY-MM-DD');
    const firstDayWeekday = (this.currentMonth.day() + 6) % 7; // mon = 0
    for (let i = 0; i < firstDayWeekday; i++) cells.push(null);
    const daysInMonth = this.currentMonth.daysInMonth();
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = this.currentMonth.clone().date(d).format('YYYY-MM-DD');
      cells.push({
        date: dateStr,
        day: d,
        selected: this.editingHolidays.has(dateStr),
        isToday: dateStr === todayStr,
      });
    }
    return cells;
  }

  toggleDay(cell: CalendarCell | null) {
    if (!cell) return;
    if (this.isEditing && !this.editingCalendar.editable) return;
    if (this.editingHolidays.has(cell.date))
      this.editingHolidays.delete(cell.date);
    else this.editingHolidays.add(cell.date);
  }

  onDelete(calendar: IHolidayCalendar) {
    if (!calendar.editable || !calendar.id) return;
    this.confirmingDelete = true;
  }

  cancelDelete() {
    this.confirmingDelete = false;
  }

  confirmDelete() {
    if (!this.editingCalendar.editable || !this.editingCalendar.id) return;
    this.deleteHolidayCalendarEvent.emit(this.editingCalendar);
  }

  onSubmit() {
    if (!this.isFormValid()) return;
    const holidays = Array.from(this.editingHolidays).sort();
    const clientId =
      this.editingCalendar.clientId === null
        ? null
        : (this.editingCalendar.clientId ?? this.clientId);
    this.action.emit({
      holidayCalendar: {
        id: this.editingCalendar.id,
        name: this.editingCalendar.name?.trim(),
        clientId,
        holidays,
      },
      isCreating: this.isCreating,
      isEditing: this.isEditing,
    });
  }

  isFormValid(): boolean {
    const nameOk = !!this.editingCalendar.name?.trim();
    const holidaysOk = this.editingHolidays.size > 0;
    const clientOk =
      this.userRole === UserRole.SUPERADMIN
        ? true
        : this.editingCalendar.clientId !== undefined &&
          this.editingCalendar.clientId !== null;
    return nameOk && holidaysOk && clientOk;
  }

  async onCreateSuccess(_created: IHolidayCalendar) {
    this.cancelEdit();
    await this.load(true);
  }

  async onEditSuccess(_updated: IHolidayCalendar) {
    this.cancelEdit();
    await this.load(true);
  }

  async onDeleteSuccess() {
    this.cancelEdit();
    await this.load(true);
  }
}
